"use client";

import { useEffect, useState, useRef } from "react";
import { useMarketplaceStore } from "@/store/marketplace-store";

/**
 * MarketplaceInitializer
 * 
 * Silent component that initializes the marketplace configuration
 * on application startup. This ensures the configuration is loaded
 * as early as possible in the application lifecycle.
 * 
 * This component doesn't render anything visible and is designed
 * to be included in the app layout.
 */
export function MarketplaceInitializer() {
  const store = useMarketplaceStore();
  const [initAttempted, setInitAttempted] = useState(false);
  const initializationInProgress = useRef(false);
  
  useEffect(() => {
    // Skip if already attempted initialization
    if (initAttempted) return;
    
    // Skip if store is already initialized
    if (store?.isInitialized) {
      console.log('[ServerInitializer] Store already initialized, skipping');
      setInitAttempted(true);
      return;
    }
    
    // Prevent multiple initialization attempts
    if (initializationInProgress.current) {
      console.log('[ServerInitializer] Initialization already in progress, skipping');
      return;
    }
    
    const initializeStore = async () => {
      // Guard against store being undefined
      if (!store) {
        console.error('[ServerInitializer] Store is undefined, cannot initialize');
        setInitAttempted(true);
        return;
      }
      
      initializationInProgress.current = true;
      
      try {
        console.log('[ServerInitializer] Starting initialization');
        
        // Check which method is available and use it
        if (typeof store.initialize === 'function') {
          console.log('[ServerInitializer] Using initialize method');
          await store.initialize();
        } else if (typeof store.fetchConfig === 'function') {
          console.log('[ServerInitializer] Using fetchConfig method (legacy)');
          await store.fetchConfig(false); // Don't bypass cache
        } else {
          console.error('[ServerInitializer] No initialization methods available');
        }
        
        console.log('[ServerInitializer] Initialization completed');
      } catch (error) {
        console.error("[ServerInitializer] Initialization failed:", error);
        
        // Even if initialization fails, mark as attempted to prevent infinite loops
        if (!store.isInitialized && typeof store.isInitialized !== 'undefined') {
          console.log('[ServerInitializer] Marking store as initialized to prevent loops');
          // @ts-ignore - Emergency override to prevent loops
          store.isInitialized = true;
        }
      } finally {
        setInitAttempted(true);
        initializationInProgress.current = false;
      }
    };
    
    // Execute initialization
    initializeStore();
    
  }, [store, initAttempted]);
  
  // This component doesn't render anything visible
  return null;
}
